import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, KeyRound, Copy, Check } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useI18n } from "i18n";
import api from "lib/api";

function DeleteConfirmModal({ app, onClose, onConfirm }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/api/client-apps/${app.id}/`);
      onConfirm();
    } catch {
      setLoading(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm mx-4 border border-border">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">{t("clientApps.deleteTitle")}</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t("clientApps.deleteBodyPrefix")} <strong>{app.name}</strong>{t("clientApps.deleteBodySuffix")}
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} loading={loading}>{t("common.remove")}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ClientApps() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [copied, setCopied] = useState("");

  const fetchApps = useCallback(async () => {
    try {
      const res = await api.get("/api/client-apps/");
      setApps(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const copy = (text, id) => {
    navigator.clipboard?.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="animate-fade-in w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("clientApps.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("clientApps.subtitle")}
          </p>
        </div>
        <Button onClick={() => navigate("/liveness/admin/client-apps/new")}>
          <Plus className="h-4 w-4 mr-2" /> {t("clientApps.new")}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : apps.length === 0 ? (
            <div className="text-center py-12">
              <KeyRound className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("clientApps.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("common.name")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("clientApps.clientId")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t("clientApps.active")}</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((app) => (
                    <tr key={app.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{app.name}</td>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                        <button onClick={() => copy(app.client_id, app.id)}
                          className="inline-flex items-center gap-1 hover:text-foreground" title={t("common.copy")}>
                          {app.client_id}
                          {copied === app.id ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${app.is_active
                          ? "bg-emerald-400/10 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
                          {app.is_active ? t("clientApps.active") : t("clientApps.inactive")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/liveness/admin/client-apps/${app.id}/edit`)}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title={t("common.edit")}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleting(app)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-600" title={t("common.remove")}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {deleting && (
        <DeleteConfirmModal app={deleting} onClose={() => setDeleting(null)}
          onConfirm={() => { setDeleting(null); fetchApps(); }} />
      )}
    </div>
  );
}
